import React, { useState, useEffect } from 'react';
import { ExternalLink, Building2, Home, Zap, Factory, Building } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categoryIcons = {
  Gewerbe: Building2,
  Industrie: Factory,
  Öffentlich: Building
};

export const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('Alle');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['Alle', ...new Set(projects.map(p => p.category))];
  
  const filteredProjects = selectedCategory === 'Alle' 
    ? projects 
    : projects.filter(p => p.category === selectedCategory);

  return (
    <section id="projekte" className="py-20 bg-slate-900">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-orange-500 font-semibold text-sm uppercase tracking-wider mb-3 block">
            Referenzen
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Unsere Projekte
            <span className="block text-gray-300">sprechen für sich</span>
          </h2>
          <p className="text-lg text-gray-400">
            Entdecken Sie eine Auswahl unserer erfolgreich realisierten Projekte
            in München und Umgebung.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-200 ${
                selectedCategory === category
                  ? 'bg-gradient-to-r from-orange-500 to-yellow-500 text-white shadow-lg'
                  : 'bg-slate-800 text-gray-300 hover:bg-slate-700'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="text-center text-gray-400 py-12">
            <div className="animate-spin w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-4">Projekte werden geladen...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, index) => {
              const IconComponent = categoryIcons[project.category] || Building2;
              return (
                <Card
                  key={project.id || index}
                  className="group bg-slate-800 border-slate-700 hover:border-orange-500 overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-orange-500/20"
                  style={{
                    animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                  }}
                >
                  {/* Image */}
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent"></div>
                    
                    {/* Category Badge */}
                    <Badge className="absolute top-4 right-4 bg-orange-500 text-white border-none">
                      <IconComponent size={14} className="mr-1" />
                      {project.category}
                    </Badge>
                  </div>

                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-white mb-3 group-hover:text-orange-400 transition-colors duration-200">
                      {project.title}
                    </h3>
                    
                    {/* Timeframe */}
                    {project.timeframe && (
                      <p className="text-sm text-gray-400 mb-3">
                        <span className="text-orange-500 font-semibold">Zeitraum:</span> {project.timeframe}
                      </p>
                    )}
                    
                    <p className="text-gray-400 leading-relaxed mb-4">
                      {project.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {filteredProjects.length === 0 && !loading && (
          <div className="text-center text-gray-400 py-12">
            <p className="text-lg">Keine Projekte in dieser Kategorie gefunden.</p>
          </div>
        )}
      </div>
    </section>
  );
};